import type { ThemeConfig } from 'antd';

/**
 * CREST AntD 主题
 *
 * 设计原则:
 *   - 单一主题色：全界面只用一个主题色表达"可操作 / 当前位置 / 焦点"，默认天蓝，
 *     用户可在个人设置切换为 Baicells 橙；主题色之外只有墨蓝文字和中性灰
 *   - 文字与带白字的按钮统一用 brand-600 / brand-700，保证白底 AA 对比度
 *   - 语义色不随主题变化：错误用玫红，与橙色主题拉开色相
 *   - 圆角按层级区分：控件 10、卡片 14、弹窗 16；阴影统一墨蓝色调
 *
 * 与 Platform 仓库 packages/web-common/src/theme/antd-theme.ts 内容一致，修改时两边同步。
 */

export type CrestThemeName = 'sky' | 'orange';

export const CREST_THEME_NAMES: readonly CrestThemeName[] = ['sky', 'orange'];
export const DEFAULT_CREST_THEME: CrestThemeName = 'sky';

export interface CrestBrandPalette {
  /** 只用于图形：发送按钮、选中短条、运行中圆点 */
  brand: string;
  /** 带白字的主按钮、链接、头像底色；AntD colorPrimary */
  brand600: string;
  /** hover、主题色文字 */
  brand700: string;
  /** 选中底色、计数徽标底、用户气泡 */
  brandTint: string;
  /** 浅色描边 */
  brandLine: string;
  /** brand-600 的 RGB 分量，用于派生透明色 */
  rgb: string;
}

// ── 品牌色板 ───────────────────────────────────────────────
export const CREST_BRAND_PALETTES: Record<CrestThemeName, CrestBrandPalette> = {
  sky: {
    brand: '#1e88e5',
    brand600: '#1570d6',
    brand700: '#0c5fb8',
    brandTint: '#e8f3ff',
    brandLine: '#a8cdf3',
    rgb: '21, 112, 214',
  },
  orange: {
    brand: '#fc4414',
    brand600: '#dc3a10',
    brand700: '#c4320b',
    brandTint: '#fff1eb',
    brandLine: '#f5b9a3',
    rgb: '220, 58, 16',
  },
};

// ── 主题无关的固定色 ────────────────────────────────────────
export const PALETTE = {
  /** 兼容旧引用：默认主题的主色 */
  primary: CREST_BRAND_PALETTES.sky.brand600,
  primaryHover: CREST_BRAND_PALETTES.sky.brand700,
  primaryActive: CREST_BRAND_PALETTES.sky.brand700,
  success: '#12a150',
  warning: '#e09100',
  error: '#d6214a',
  // 中性色 — 墨蓝色调
  bg: '#f0f2f5',
  bgElevated: '#ffffff',
  mist: '#f6f7f9',
  border: '#e2e6ec',
  borderSubtle: '#eceff3',
  text: '#0f2747',
  textBody: '#3a4556',
  textSecondary: '#5f6b7d',
  textTertiary: '#8a94a6',
} as const;

// ── 阴影分级（4 档，墨蓝色调）──────────────────────────────
const SHADOWS = {
  xs: '0 1px 2px 0 rgba(15, 39, 71, 0.05)',
  sm: '0 1px 2px rgba(15, 39, 71, 0.04), 0 4px 12px -6px rgba(15, 39, 71, 0.12)',
  md: '0 1px 2px rgba(15, 39, 71, 0.04), 0 8px 24px -12px rgba(15, 39, 71, 0.18)',
  lg: '0 2px 6px rgba(15, 39, 71, 0.04), 0 24px 48px -24px rgba(15, 39, 71, 0.3)',
} as const;

export function normalizeCrestTheme(value: unknown): CrestThemeName {
  return value === 'orange' || value === 'sky' ? value : DEFAULT_CREST_THEME;
}

export function createCrestTheme(name: CrestThemeName = DEFAULT_CREST_THEME): ThemeConfig {
  const brand = CREST_BRAND_PALETTES[normalizeCrestTheme(name)];
  const alpha = (value: number) => `rgba(${brand.rgb}, ${value})`;
  return {
    token: {
      // ── 颜色
      colorPrimary: brand.brand600,
      colorPrimaryHover: brand.brand700,
      colorPrimaryActive: brand.brand700,
      colorLink: brand.brand600,
      colorLinkHover: brand.brand700,
      colorLinkActive: brand.brand700,
      colorInfo: brand.brand600,
      colorSuccess: PALETTE.success,
      colorWarning: PALETTE.warning,
      colorError: PALETTE.error,

      colorBgLayout: PALETTE.bg,
      colorBgContainer: PALETTE.bgElevated,
      colorBgElevated: PALETTE.bgElevated,

      colorTextBase: PALETTE.text,
      colorText: PALETTE.text,
      colorTextSecondary: PALETTE.textSecondary,
      colorTextTertiary: PALETTE.textTertiary,

      colorBorder: PALETTE.border,
      colorBorderSecondary: PALETTE.borderSubtle,

      // ── 圆角
      borderRadius: 10,
      borderRadiusLG: 14,
      borderRadiusSM: 8,
      borderRadiusXS: 4,

      // ── 字体
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "PingFang SC", "HarmonyOS Sans SC", "Hiragino Sans GB", "Microsoft YaHei", "Segoe UI", Inter, sans-serif',
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 12,
      lineHeight: 1.5715,

      // ── 控件尺寸
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,

      // ── 阴影
      boxShadow: SHADOWS.sm,
      boxShadowSecondary: SHADOWS.md,
      boxShadowTertiary: SHADOWS.xs,

      // ── 间距
      paddingContentHorizontal: 16,
      paddingContentVertical: 10,

      // ── 动画
      motionDurationMid: '0.2s',
      motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },

    components: {
      // ── Modal: 大圆角 + 柔和阴影
      Modal: {
        borderRadiusLG: 16,
        paddingMD: 20,
        paddingContentHorizontalLG: 28,
        titleFontSize: 18,
        titleLineHeight: 1.5,
        headerBg: 'transparent',
        contentBg: PALETTE.bgElevated,
        footerBg: 'transparent',
        boxShadow: SHADOWS.lg,
      },

      // ── Drawer: 同 Modal
      Drawer: {
        paddingLG: 24,
        colorBgElevated: PALETTE.bgElevated,
      },

      // ── Card: 软圆角 + 移除多余分隔线
      Card: {
        borderRadiusLG: 14,
        paddingLG: 20,
        headerBg: 'transparent',
        headerFontSize: 15,
        headerHeight: 52,
        colorBorderSecondary: PALETTE.borderSubtle,
        boxShadow: SHADOWS.xs,
        boxShadowTertiary: SHADOWS.xs,
      },

      // ── Button: 主按钮带主题色投影，hover 取更深一档仍满足 AA
      Button: {
        borderRadius: 10,
        borderRadiusLG: 12,
        borderRadiusSM: 8,
        controlHeight: 36,
        fontWeight: 500,
        primaryShadow: `0 6px 14px -8px ${alpha(0.7)}`,
        defaultShadow: 'none',
        dangerShadow: 'none',
        paddingInline: 14,
        paddingInlineLG: 18,
      },

      // ── Table: 浅头 + 行高 + 行 hover + 去列线
      Table: {
        headerBg: PALETTE.mist,
        headerColor: PALETTE.textSecondary,
        headerSplitColor: 'transparent',
        headerBorderRadius: 12,
        borderColor: PALETTE.borderSubtle,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        rowHoverBg: PALETTE.mist,
        rowSelectedBg: brand.brandTint,
        rowSelectedHoverBg: alpha(0.12),
        footerBg: 'transparent',
      },

      // ── Input/Select/DatePicker: 36px 高 + 主题色焦点光环
      Input: {
        controlHeight: 36,
        borderRadius: 10,
        activeShadow: `0 0 0 3px ${alpha(0.14)}`,
        hoverBorderColor: brand.brandLine,
        activeBorderColor: brand.brand600,
      },
      InputNumber: {
        controlHeight: 36,
        borderRadius: 10,
        activeShadow: `0 0 0 3px ${alpha(0.14)}`,
      },
      Select: {
        controlHeight: 36,
        borderRadius: 10,
        optionSelectedBg: brand.brandTint,
        optionActiveBg: PALETTE.mist,
      },
      DatePicker: {
        controlHeight: 36,
        borderRadius: 10,
        activeShadow: `0 0 0 3px ${alpha(0.14)}`,
      },
      Cascader: {
        controlItemBgActive: brand.brandTint,
        controlItemBgHover: PALETTE.mist,
      },

      // ── Tabs: 主题色下划线
      Tabs: {
        itemSelectedColor: brand.brand700,
        inkBarColor: brand.brand,
        titleFontSize: 14,
        horizontalItemPadding: '12px 0',
        horizontalItemGutter: 28,
        itemHoverColor: brand.brand700,
      },

      // ── Tag: 软圆角 + 浅底
      Tag: {
        borderRadiusSM: 6,
        defaultBg: PALETTE.mist,
        defaultColor: PALETTE.textBody,
        fontSize: 12,
      },

      // ── Menu: 选中态白色抬升 + 主题色文字
      Menu: {
        itemBorderRadius: 10,
        itemHeight: 38,
        itemColor: PALETTE.textBody,
        itemSelectedBg: brand.brandTint,
        itemSelectedColor: brand.brand700,
        itemHoverBg: 'rgba(15, 39, 71, 0.05)',
        itemHoverColor: PALETTE.text,
        itemActiveBg: alpha(0.08),
        subMenuItemBg: 'transparent',
        iconSize: 16,
        collapsedIconSize: 18,
      },

      // ── Form: label 与控件间距
      Form: {
        labelFontSize: 13,
        labelColor: PALETTE.textBody,
        itemMarginBottom: 18,
        verticalLabelPadding: '0 0 6px',
      },

      // ── Tooltip
      Tooltip: {
        borderRadius: 8,
        colorBgSpotlight: PALETTE.text,
        paddingXS: 8,
      },

      // ── Badge / Notification / Message
      Badge: {
        colorBgContainer: PALETTE.error,
        indicatorHeight: 18,
        indicatorHeightSM: 12,
      },
      Notification: {
        borderRadiusLG: 14,
        paddingMD: 16,
        boxShadow: SHADOWS.lg,
      },
      Message: {
        borderRadiusLG: 12,
        boxShadow: SHADOWS.md,
      },

      // ── Steps: BPF 进度条 / 立项流程
      Steps: {
        colorPrimary: brand.brand600,
        titleLineHeight: 1.4,
        iconSize: 28,
        iconFontSize: 14,
      },

      // ── Segmented: 白色抬升的选中块
      Segmented: {
        itemSelectedColor: PALETTE.text,
        itemColor: PALETTE.textSecondary,
        trackBg: PALETTE.mist,
        borderRadius: 999,
        borderRadiusSM: 999,
      },

      // ── Layout (Sider/Header)
      Layout: {
        headerBg: PALETTE.bg,
        headerHeight: 56,
        headerPadding: '0 20px',
        siderBg: PALETTE.bg,
        bodyBg: PALETTE.bg,
        footerBg: PALETTE.bg,
      },

      // ── Popover / Dropdown
      Popover: {
        borderRadiusLG: 12,
        boxShadow: SHADOWS.lg,
      },
      Dropdown: {
        borderRadiusLG: 12,
        borderRadiusSM: 8,
        controlItemBgHover: PALETTE.mist,
        controlItemBgActive: brand.brandTint,
      },

      // ── Empty
      Empty: {
        colorTextDescription: PALETTE.textSecondary,
      },
    },
  };
}

const THEME_CACHE = new Map<CrestThemeName, ThemeConfig>();

/** 返回缓存的主题对象，保证同一主题引用稳定，避免 ConfigProvider 无谓重算样式。 */
export function crestThemeFor(name: CrestThemeName | null | undefined): ThemeConfig {
  const key = normalizeCrestTheme(name);
  let config = THEME_CACHE.get(key);
  if (!config) {
    config = createCrestTheme(key);
    THEME_CACHE.set(key, config);
  }
  return config;
}

/** 默认（天蓝）主题；兼容旧的静态引用。 */
export const crestTheme: ThemeConfig = crestThemeFor(DEFAULT_CREST_THEME);
